import React, {forwardRef, PropsWithChildren, useRef, useState} from "react";
import classNames from "classnames";

import {useAutoHeight} from "../hooks/useAutoHeight";

import {content, scrollBottomShadow, scrollShadow, scrollShadowHide, scrollTopShadow, wrapper} from "./AdvancedScroll.module.css";

interface IBaseProps extends PropsWithChildren {
    scrollShadowHeight?: number;
    hideTopShadowAtScrollTop?: number;
    hideBottomShadowAtScrollLeft?: number;
    scrollTopShadowClassName?: string;
    scrollBottomShadowClassName?: string;
    contentClassName?: string;
    disableScroll?: boolean;
    className?: string;
}

interface IPropsWithHeight extends IBaseProps {
    height: number;
    autoHeight?: never;
}

interface IPropsWithAutoHeight extends IBaseProps {
    height?: never;
    autoHeight: true;
}

type IProps = IPropsWithHeight | IPropsWithAutoHeight;

export const AdvancedScroll = forwardRef<HTMLDivElement, IProps>((props, wrapperRef) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [hideTopShadow, setHideTopShadow] = useState(true);
    const [hideBottomShadow, setHideBottomShadow] = useState(false);

    const {combinedWrapperRef, currentHeight} = useAutoHeight({
        autoHeight: !!props.autoHeight,
        initialHeight: props.height || 0,
        wrapperRef
    });

    const handleScroll = () => {
        const scrollHeight = contentRef.current?.scrollHeight || 0;
        const scrollPosition = contentRef.current?.scrollTop || 0;
        const scrollLeft = scrollHeight - currentHeight - scrollPosition;
        const hideTopShadowAtScrollTop = props.hideTopShadowAtScrollTop || 0;
        const hideBottomShadowAtScrollLeft = props.hideBottomShadowAtScrollLeft || 0;

        setHideTopShadow(scrollPosition === hideTopShadowAtScrollTop);
        setHideBottomShadow(scrollLeft <= hideBottomShadowAtScrollLeft);
    };

    return (
        <div ref={combinedWrapperRef} className={classNames(wrapper, props.className)} style={props.disableScroll ? {} : {height: `${currentHeight}px`}}>
            {props.disableScroll ? (
                <div className={props.contentClassName}>{props.children}</div>
            ) : (
                <div ref={contentRef} onScroll={handleScroll} className={classNames(content, props.contentClassName)}>
                    {props.children}
                </div>
            )}
            {props.disableScroll ? null : (
                <>
                    <div
                        className={classNames(scrollShadow, scrollTopShadow, props.scrollTopShadowClassName, hideTopShadow ? scrollShadowHide : undefined)}
                        style={{height: `${props.scrollShadowHeight || 70}px`}}
                    />
                    <div
                        className={classNames(
                            scrollShadow,
                            scrollBottomShadow,
                            props.scrollBottomShadowClassName,
                            hideBottomShadow ? scrollShadowHide : undefined
                        )}
                        style={{height: `${props.scrollShadowHeight || 70}px`}}
                    />
                </>
            )}
        </div>
    );
});
